import React, { useEffect, useMemo } from 'react';

import {
    AcknowledgeChangeInput,
    GetPaperPlanChangesInput,
    GetPaperPlanChangesOutput,
    GetPaperPlanMetadataInput,
    GetPaperPlanMetadataOutput,
    RoyaltyModelsEnum
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';

import { ColumnState, GridApi, IDetailCellRendererParams } from 'ag-grid-community';
import { Link, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import {
    AcknowledgedStatus,
    DEFAULT_TOASTER_TIMEOUT,
    MultiplierName,
    PaperPlanRateGroup,
    RateMultiplierDisplayName
} from 'src/constants';
import {
    DEFAULT_COLUMN_DEFINITIONS,
    DEFAULT_EXCEL_EXPORT_CONFIG,
    DEFAULT_GROUP_DISPLAY_TYPE,
    DEFAULT_ROW_SELECTION,
    DEFAULT_STATUS_BAR,
    EXCEL_DATE_STYLE_ID,
    LINK_COLOR
} from 'src/constants/agGrid';
import { TERRITORY_DEPENDENT } from 'src/constants/paperPlans';
import { EDITOR_ROLE_SET, VIEWER_ROLE_SET } from 'src/constants/role';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IPlanValues } from 'src/types/IPaperPlan';
import { IPaperPlanChangesRowData, IRateChangePerPaperPlan } from 'src/types/IPaperPlanChanges';
import { IToast } from 'src/types/IToaster';
import { getCorePlan, getPlanAttributes, getPlanGroup, getRateGroup } from 'src/util/PaperPlans/PaperPlanUtils';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getStatusForAcknowledgedChanges, getYYYYMMDDStrFromTimestamp } from 'src/util/grid/cellGetter';
import { getPhoneToolLinkCellRenderer } from 'src/util/grid/cellRenderer';
import { dateFilterOptions } from 'src/util/grid/filter';
import { dateComparator, dateFilterComparator } from 'src/util/grid/sort';

import { BaseAgGrid } from '../BaseAgGrid';
import { FilterInput } from '../FilterInput';
import { ProtectedRoute } from '../ProtectedRoute';
import {
    CalcType,
    absoluteOfWholeAndUserPrice,
    isIntroPlan,
    isPositiveNumber
} from '../RateCardDetail/ThirdPartyRates/ThirdPartyRatesAutoCalcUtil';
import './ReviewPaperPlan.scss';

export const ReviewPaperPlan = () => {
    const { path } = useRouteMatch();
    const { pathname } = useLocation();
    const { setTopAlertMsg, userRole, userInfo, setToasts } = React.useContext(AppContext);
    const [filterText, setFilterText] = React.useState('');
    const [isDataLoaded, setIsDataLoaded] = React.useState(false);
    const [agGridApi, setAgGridApi] = React.useState<GridApi>();
    const [reviewPlanRowData, setReviewPlanRowData] = React.useState<IPaperPlanChangesRowData[]>([]);

    const reviewPlanColDefs = [
        {
            field: 'paperPlanName',
            headerName: 'PAPER Plan ID',
            filter: 'agSetColumnFilter',
            minwidth: 300,
            flex: 2,
            cellRenderer: 'agGroupCellRenderer',
            cellRendererParams: {
                innerRenderer: (params: any) => {
                    return (
                        <Link
                            style={{ color: LINK_COLOR, textDecoration: 'none' }}
                            to={`${ROUTES.PAPER_PLANS.INDEX}/${params.data.paperPlanId}`}
                        >
                            {params.value}
                        </Link>
                    );
                }
            }
        },
        {
            field: 'paperPlanDisplayName',
            headerName: 'PAPER Plan Friendly Name',
            filter: 'agSetColumnFilter',
            minwidth: 300,
            flex: 2
        },
        {
            field: 'planGroup',
            headerName: 'Plan Group',
            filter: 'agSetColumnFilter',
            flex: 1
        },
        {
            field: 'rateGroup',
            headerName: 'Rate Group',
            filter: 'agSetColumnFilter',
            flex: 1
        },
        {
            field: 'creationDate',
            headerName: 'Creation Date',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'greaterThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            cellClass: EXCEL_DATE_STYLE_ID,
            valueFormatter: emptyCellFormatter,
            valueGetter: getYYYYMMDDStrFromTimestamp,
            flex: 1
        },
        {
            field: 'createdBy',
            headerName: 'Created By',
            filter: 'agSetColumnFilter',
            cellRenderer: getPhoneToolLinkCellRenderer,
            flex: 1
        },
        {
            field: 'status',
            headerName: 'Status',
            valueGetter: getStatusForAcknowledgedChanges
        },
        {
            field: 'actions',
            headerName: 'Actions',
            minWidth: 200,
            cellRenderer: (params: any) => (
                <Column height="100%" alignmentVertical="center">
                    <Row>
                        {!params.data.isAcknowledged && (
                            <Button
                                className="acknowledge-button"
                                size="small"
                                type="link"
                                onClick={() => handleAcknowledgeBtnClick(params.data)}
                                disabled={!EDITOR_ROLE_SET.has(userRole)}
                            >
                                Mark as Reviewed
                            </Button>
                        )}
                    </Row>
                </Column>
            )
        }
    ];

    const detailRateColDefs = [
        {
            field: 'contractName',
            headerName: 'Contract Name',
            filter: 'agSetColumnFilter',
            width: 300,
            cellRenderer: (params: IDetailCellRendererParams) => {
                return (
                    <Link
                        style={{ color: LINK_COLOR, textDecoration: 'none' }}
                        to={`${ROUTES.RATE_CARDS.INDEX}/${params.data.contractId}`}
                    >
                        {params.value}
                    </Link>
                );
            }
        },
        {
            field: 'corePlan',
            headerName: 'Core Plan',
            filter: 'agSetColumnFilter',
            flex: 1
        },
        {
            field: 'rateType',
            headerName: 'Rate Type',
            filter: 'agSetColumnFilter',
            flex: 1
        },
        {
            field: 'rate',
            headerName: 'Rate',
            filter: 'agSetColumnFilter',
            flex: 1
        },
        {
            field: 'multiplierName',
            headerName: 'Multiplier Name',
            filter: 'agSetColumnFilter',
            flex: 1
        },
        {
            field: 'multiplierValue',
            headerName: 'Multiplier Value',
            filter: 'agSetColumnFilter',
            flex: 1
        },
        {
            field: 'calcType',
            headerName: 'Calc Type',
            filter: 'agSetColumnFilter',
            flex: 1
        }
    ];

    useEffect(() => {
        if (pathname !== ROUTES.REVIEW_PAPER_PLANS.INDEX) return;
        getApiResponse();
    }, [pathname]);

    const getMultiplierName = (multiplierName: string) => {
        switch (multiplierName) {
            case MultiplierName.ANNUAL:
                return RateMultiplierDisplayName.ANNUAL;
            case MultiplierName.DAILY:
                return RateMultiplierDisplayName.DAILY;
            case MultiplierName.WEEKLY:
                return RateMultiplierDisplayName.WEEKLY;
            case MultiplierName.PRICE_DROP_3M:
                return RateMultiplierDisplayName.PRICE_DROP_3M;
            case MultiplierName.PRICE_DROP_4M:
                return RateMultiplierDisplayName.PRICE_DROP_4M;
            case MultiplierName.PRICE_DROP_6M:
                return RateMultiplierDisplayName.PRICE_DROP_6M;
            case MultiplierName.WINBACK:
                return RateMultiplierDisplayName.WINBACK;
            case MultiplierName.EXTENDED_TRIAL_2M:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_2M;
            case MultiplierName.EXTENDED_TRIAL_3M:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_3M;
            case MultiplierName.EXTENDED_TRIAL_4M:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_4M;
            case MultiplierName.EXTENDED_TRIAL_5M:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_5M;
            case MultiplierName.EXTENDED_TRIAL_6M:
                return RateMultiplierDisplayName.EXTENDED_TRIAL_6M;
            case MultiplierName.ASD_EXTENDED_TRIAL_2M:
                return RateMultiplierDisplayName.ASD_EXTENDED_TRIAL_2M;
            case MultiplierName.ASD_EXTENDED_TRIAL_3M:
                return RateMultiplierDisplayName.ASD_EXTENDED_TRIAL_3M;
            default:
                return RateMultiplierDisplayName.NONE;
        }
    };

    const mapPaperPlanRowData = (response: GetPaperPlanChangesOutput) => {
        const groupedData: IPaperPlanChangesRowData[] = [];
        const planToContractSet: Map<number, Set<number>> = new Map();
        response.paperPlanChanges?.map((paperPlanChange) => {
            if (paperPlanChange.rateCardChanges.length !== 0) {
                const derivedMultiplierName = getMultiplierName(
                    paperPlanChange.rateCardChanges[0].multiplierName || ''
                );

                const rateCardChangeList = {
                    contractId: paperPlanChange.rateCardChanges[0].contractId || '',
                    contractName: paperPlanChange.rateCardChanges[0].contractName || '',
                    multiplierName: derivedMultiplierName,
                    multiplierValue:
                        derivedMultiplierName == RateMultiplierDisplayName.NONE
                            ? 1
                            : paperPlanChange.rateCardChanges[0].multiplierValue || '',
                    corePlanPSM: paperPlanChange.rateCardChanges[0].corePlanPSM || 0,
                    psmDiscCap: paperPlanChange.rateCardChanges[0].psmDiscCap || 0
                } as IRateChangePerPaperPlan;

                const ungroupedPaperPlanChange = {
                    changeId: paperPlanChange.changeId || '',
                    paperPlanId: paperPlanChange.paperPlanId || '',
                    paperPlanName: paperPlanChange.paperPlanName || '',
                    paperPlanDisplayName: paperPlanChange.paperPlanDisplayName || '',
                    creationDate: paperPlanChange.creationDate?.toString() || '',
                    createdBy: paperPlanChange.createdBy || '',
                    isAcknowledged: paperPlanChange.isAcknowledged,
                    acknowledgedBy: paperPlanChange.acknowledgedBy || '',
                    acknowledgedDate: paperPlanChange.acknowledgedDate?.toString() || '',
                    rateCardChanges: [rateCardChangeList]
                } as IPaperPlanChangesRowData;
                const groupedPaperPlanChange = groupedData.find(
                    (data) => data.changeId === ungroupedPaperPlanChange.changeId
                );
                if (groupedPaperPlanChange) {
                    if (
                        !planToContractSet
                            .get(ungroupedPaperPlanChange.changeId)
                            ?.has(ungroupedPaperPlanChange.rateCardChanges[0].contractId)
                    ) {
                        groupedPaperPlanChange.rateCardChanges.push(...ungroupedPaperPlanChange.rateCardChanges);
                    }
                    planToContractSet
                        .get(ungroupedPaperPlanChange.changeId)
                        ?.add(ungroupedPaperPlanChange.rateCardChanges[0].contractId);
                } else {
                    groupedData.push(ungroupedPaperPlanChange);
                    planToContractSet.set(ungroupedPaperPlanChange.changeId, new Set());
                    planToContractSet
                        .get(ungroupedPaperPlanChange.changeId)
                        ?.add(ungroupedPaperPlanChange.rateCardChanges[0].contractId);
                }
            }
        });
        return groupedData;
    };

    const mapCalculatedRowData = (
        changesOutput: GetPaperPlanChangesOutput,
        metadataOutput: GetPaperPlanMetadataOutput
    ) => {
        const paperPlanChanges = mapPaperPlanRowData(changesOutput);
        const newReviewPlanRowData = paperPlanChanges?.map((apiData) => {
            const paperPlanId = apiData.paperPlanId;
            const paperPlanMetaData = metadataOutput.planMetadata?.find(
                (plan) => plan.paperPlan?.paperPlanId === paperPlanId
            );
            const attributes = getPlanAttributes(paperPlanMetaData?.attributeSet?.[0].attributes || []);
            const reviewPlanRowData = {
                ...apiData,
                employeePlan: attributes.employeePlan,
                carrierPlan: attributes.carrierPlan,
                royaltyBearing: attributes.royaltyBearing,
                customerPays: attributes.customerPays,
                renewalCadence: attributes.renewalCadence,
                promoDurationInDays: attributes.promoDurationInDays
            };

            const planGroup = getPlanGroup(reviewPlanRowData as unknown as IPlanValues);
            const rateGroup = getRateGroup(reviewPlanRowData as unknown as IPlanValues);

            reviewPlanRowData.rateCardChanges.forEach((rateCard: IRateChangePerPaperPlan) => {
                let calcType;
                let rate = TERRITORY_DEPENDENT;
                if (rateGroup == PaperPlanRateGroup.CarrierIntro || rateGroup == PaperPlanRateGroup.CarrierStandard) {
                    if (isIntroPlan(rateGroup)) {
                        calcType = CalcType.Derived;
                    } else {
                        if (isPositiveNumber(attributes.wholesalePrice) && isPositiveNumber(attributes.endUserPrice)) {
                            calcType = CalcType.AutoCalc;
                        } else {
                            calcType = CalcType.SelectType;
                        }
                    }
                } else {
                    calcType = CalcType.Derived;
                }

                if (rateGroup == PaperPlanRateGroup.CarrierIntro || rateGroup == PaperPlanRateGroup.CarrierStandard) {
                    if (!isIntroPlan(planGroup.toString()) && calcType === CalcType.AutoCalc) {
                        rate = (
                            (rateCard.corePlanPSM as unknown as number) *
                            (1 -
                                Math.min(
                                    rateCard.psmDiscCap as unknown as number,
                                    // @ts-ignore
                                    absoluteOfWholeAndUserPrice(attributes.wholesalePrice, attributes.endUserPrice)
                                ))
                        ).toString();
                    } else if (calcType === CalcType.Derived) {
                        // @ts-ignore
                        rate = (rateCard.corePlanPSM * rateCard.multiplierValue).toString();
                    }
                }

                rateCard.rateType = RoyaltyModelsEnum.PSM;
                rateCard.rate = rate;
                rateCard.calcType = calcType;
                rateCard.corePlan = getCorePlan(attributes as IPlanValues);
            });

            return {
                ...reviewPlanRowData,
                planGroup: planGroup,
                rateGroup: rateGroup
            };
        });
        return newReviewPlanRowData as IPaperPlanChangesRowData[];
    };

    const detailCellRenderParams = useMemo(() => {
        return {
            detailGridOptions: {
                rowSelection: DEFAULT_ROW_SELECTION,
                defaultColDef: DEFAULT_COLUMN_DEFINITIONS,
                groupDisplayType: DEFAULT_GROUP_DISPLAY_TYPE,
                statusBar: DEFAULT_STATUS_BAR,
                excelStyles: DEFAULT_EXCEL_EXPORT_CONFIG,
                accentedSort: true,
                columnDefs: detailRateColDefs
            },
            getDetailRowData: (params) => {
                params.successCallback(params.data.rateCardChanges);
            }
        } as IDetailCellRendererParams<IPaperPlanChangesRowData, IRateChangePerPaperPlan>;
    }, []);

    const reviewPlanColSortModel: ColumnState[] = [{ colId: 'creationDate', sort: 'desc', sortIndex: 0 }];

    const setDefaultFilter = () => {
        const statusFilterModel = {
            status: {
                filterType: 'set',
                values: [AcknowledgedStatus.NOTACKNOWLEDGED]
            }
        };
        agGridApi?.setFilterModel(statusFilterModel);
    };

    useEffect(() => {
        if (agGridApi) setDefaultFilter();
    }, [agGridApi]);

    const handleAcknowledgeBtnClick = (changesRowData: IPaperPlanChangesRowData) => {
        contractConfigurationClient
            .acknowledgeChange(
                AcknowledgeChangeInput.clone({
                    changeId: changesRowData.changeId,
                    acknowledgedBy: userInfo.username
                })
            )
            .subscribe(
                () => {
                    setIsDataLoaded(false);

                    const toastMessage = `Change Acknowledged`;
                    const toast: IToast = {
                        id: 'acknowledge-change-toast',
                        timeout: DEFAULT_TOASTER_TIMEOUT * 2,
                        message: toastMessage
                    };
                    setToasts([toast]);

                    getApiResponse();
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    };

    const getApiResponse = () => {
        const paperPlanChangesPromise = contractConfigurationClient
            .getPaperPlanChanges(GetPaperPlanChangesInput.clone({}))
            .toPromise();

        const paperPlanMetadataPromise = contractConfigurationClient
            .getPaperPlanMetadata(GetPaperPlanMetadataInput.clone({ serviceIds: [1] }))
            .toPromise();

        Promise.all([paperPlanChangesPromise, paperPlanMetadataPromise])
            .then((response) => {
                const [paperPlanChangesResponse, paperPlanMetadataResponse] = response;
                setReviewPlanRowData(
                    mapCalculatedRowData(paperPlanChangesResponse, paperPlanMetadataResponse) || undefined
                );
                setIsDataLoaded(true);
            })
            .catch((error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error('APIs Unexpected error', error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            });
    };

    const reviewPaperPlanContent = (
        <Column className="review-paper-plans-div" spacing="300">
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                </Row>
            </Box>
            <div className="review-paper-plans-grid">
                {isDataLoaded ? (
                    <BaseAgGrid
                        columnDefs={reviewPlanColDefs}
                        sortModel={reviewPlanColSortModel}
                        rowData={reviewPlanRowData}
                        filterText={filterText}
                        setAgGridApi={setAgGridApi}
                        onFirstDataRendered={setDefaultFilter}
                        masterDetail={true}
                        detailCellRendererParams={detailCellRenderParams}
                        detailRowAutoHeight={true}
                    />
                ) : (
                    <Loader />
                )}
            </div>
        </Column>
    );

    return (
        <div className="review-paper-plans-container">
            <Switch>
                <ProtectedRoute exact requiredRoleSet={VIEWER_ROLE_SET} path={path}>
                    {reviewPaperPlanContent}
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
